import { useState } from "react";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    type DialogTriggerProps,
} from "@/components/ui/dialog.tsx";
import Icon from "@/components/ui/icon";
import DownloadLink from "@/features/downloads/components/download-link.tsx";
import DownloadTrigger from "@/features/downloads/components/download-trigger.tsx";
import DynamicSpecSheet from "@/features/downloads/components/dynamic-spec-sheet.tsx";
import useProductDownloads from "@/features/downloads/hooks/use-product-downloads.ts";
import { DYNAMIC_SPEC_SHEET } from "@/features/downloads/resources/constants.ts";
import useModelCode from "@/features/sub-families/hooks/use-model-code.ts";
import useTracking from "@/features/tracking/hooks/use-tracking.ts";
import { useTranslations } from "@/features/translations/components/translations";
import { useAppSelector } from "@/redux/store.ts";

type Props = Omit<DialogTriggerProps, "children" | "onClick">;
export default function TechnicalFiles(props: Props) {
    const t = useTranslations();

    const downloads = useProductDownloads();
    const modelCode = useModelCode();
    const trackEvent = useTracking();

    const smartNumber = useAppSelector((state) => state.smartNumber.calculatedResult);

    const [open, setOpen] = useState<boolean>(false);

    const trackTechnicalFiles = () => {
        trackEvent("3d-nav-technical", {
            component: "navigation",
            label: "technical_files",
            model_code: modelCode ?? "",
        });
    };

    const trackTechnicalAsset = (assetType: string) => {
        return () => {
            trackEvent("3d-technical", {
                component: "modal",
                label: assetType,
                model_code: modelCode ?? "",
            });
        };
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger {...props} variant="action" background="accent" onClick={trackTechnicalFiles}>
                <span>{t("technical_files")}</span>
                <Icon name="download" />
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t("download_technical_files")}</DialogTitle>
                </DialogHeader>
                <DialogBody>
                    <div className="downloads">
                        <DynamicSpecSheet
                            calculatedSmartNumber={smartNumber}
                            onClick={trackTechnicalAsset(DYNAMIC_SPEC_SHEET)}
                        />
                        {downloads.map(({ url, subType }) => {
                            return (
                                <DownloadLink key={subType} fileUrl={url} onClick={trackTechnicalAsset(subType)}>
                                    <Icon name="file-download" />
                                    <span>{t(subType)}</span>
                                </DownloadLink>
                            );
                        })}
                    </div>
                </DialogBody>
                <DialogFooter>
                    <DownloadTrigger className="downloads__trigger" background="dark" onSuccess={() => setOpen(false)}>
                        {t("download_all")}
                    </DownloadTrigger>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
